import { useState, useEffect, memo, useRef } from "react";
import NavBar from "./components/NavBar";
import { getAsset, getLabel } from "../../utils/appConfig";
import { FaHeart } from "react-icons/fa";
import ForYouSection from "./components/ForYouSection";
import ContentTypeSection from "./components/ContentTypeSection";
import { VscSettings } from "react-icons/vsc";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getLikeBookmarkedContent,
  getMasterData,
} from "./service";
import { IoPlay } from "react-icons/io5";
import { PiCardsThreeFill, PiHeadphonesFill } from "react-icons/pi";
import { FaFile } from "react-icons/fa6";
import useStore from "../../store/useStore";
import { mintPediaToken } from "./pediaToken";
import { getDecodedToken } from "../../utils/localStorage";
import NewContentTypeSection from "./components/NewContentTypeSection";
import { ReactComponent as Edit} from "./../../assets/images/ddx-edit.svg"; 
import { trackZymedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";


interface SpecialityItem {
  id: number;
  code: string;
  displayName: string;
  pmMasterId?: number;
  pmEnabled: boolean;
}

interface ContinuumItem {
  id: number;
  code: string;
  depends?: string;
  pmEnabled: boolean;
}

interface ContentGenreItem {
  id: number;
  code: string;
  depends: string;
  pmEnabled: boolean;
}

interface ContentTypeItem {
  id: number;
  code: string;
  sequence: number;
}

interface TatvaPediaFilters {
  "Content Type": ContentTypeItem[];
  Genre: ContentGenreItem[];
  Continuum: ContinuumItem[];
  Speciality: SpecialityItem[];
  Interestpreference: ContentTypeItem[];
  ConsolidatedModule: ContentTypeItem[];
}

function TatvaPediaHomeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  // Get the appropriate logo based on app configuration
  const pediaLogo = getAsset("pedia");
  const pediaLabel = getLabel("pediaLabel");

  const {
    tatvaPediaToken,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
    setTatvaPediaToken,
    tatvaPediaFilters,
    setTatvaPediaFilters,
  } = useStore();

  const tokenData: any = getDecodedToken();
  const doctorDetails: any = tokenData?.user || {};

  // Define tab types based on content types plus "latest"
  type TabType = "latest" | string;

  // Get activeTab from URL query parameter if present
  const params = new URLSearchParams(location.search);
  const tabFromUrl = decodeURIComponent(params.get("activeTab") || "");

  // Set initial active tab from URL parameter or default to "latest"
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl || "latest");

  /** Pill tabs across the top are "tab switched"; the chevron dropdown is a category
   * filter. Both set the same state, so they are tracked at the tap site. */
  const selectTabFromPills = (tab: TabType) => {
    if (tab !== activeTab) trackZymedEvent(ZYMED_EVENTS.PEDIA_TAB_SWITCHED, { tab_name: tab });
    setActiveTab(tab);
  };
  const selectTabFromDropdown = (tab: TabType) => {
    trackZymedEvent(ZYMED_EVENTS.PEDIA_CATEGORY_FILTER_SELECTED, { category_name: tab });
    setActiveTab(tab);
  };
  
  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Ref for dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update activeTab when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = decodeURIComponent(params.get("activeTab") || "");

    if (tabFromUrl) {
      setActiveTab(tabFromUrl);

      // Clean up the URL by removing the query parameters after setting the active tab
      navigate("/tatvapedia", { replace: true });
    }
  }, [location.search, navigate]);

  // Initialize token on component mount if not already present
  useEffect(() => {
    const initializeToken = async () => {
      if (!tatvaPediaToken) {
        try {
          // Shared with PediaTrendingSection so the two never mint separate tokens.
          const token = await mintPediaToken();
          if (token) setTatvaPediaToken(token);
        } catch (error) {
          console.error("Failed to initialize TatvaPedia token:", error);
        }
      }
    };

    initializeToken();
  }, [tatvaPediaToken, doctorDetails?.mobileNumber]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await getMasterData([
          "ContentType",
          "ContentGenre",
          "Continuum",
          "Speciality",
          "Interestpreference",
          "ConsolidatedModule"
        ]);

        // Map API response to our filter structure with full objects
        const mappedData: TatvaPediaFilters = {
          "Content Type": response.contenttype || [],
          Speciality: response.speciality || [],
          Genre: response.contentgenre || [],
          Continuum: response.continuum || [],
          Interestpreference: response.interestpreference || [],
          ConsolidatedModule: response.consolidatedmodule || [],
        };
        // Update the store with the full objects
        setTatvaPediaFilters(mappedData);
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };

    fetchFilters();
  }, [setTatvaPediaFilters]);

  // Get content types from filters
  const contentTypes = tatvaPediaFilters["ConsolidatedModule"] || [];

  // Define the new content types based on the provided object
  const newContentTypes = [
    {
      id: 6003,
      code: "Clinical Advances & Conference Updates",
      displayName: "Clinical Updates",
      pmEnabled: false
    },
    {
      id: 6001,
      code: "Clinical Guidelines & Protocols",
      displayName: "Guidelines & Protocols",
      pmEnabled: false
    },
    {
      id: 6004,
      code: "Educational Resources",
      displayName: "Skill Building",
      pmEnabled: false
    },
    {
      id: 6005,
      code: "Expert Insights & Opinion",
      displayName: "Expert Insights",
      pmEnabled: false
    },
    {
      id: 6002,
      code: "Patient Case Studies & Real-world Evidence",
      displayName: "Case Studies & Evidence",
      pmEnabled: false
    },
    {
      id: 6006,
      code: "Patient Communication Resources",
      displayName: "Patient Communication",
      pmEnabled: false
    },
    {
      id: 6007,
      code: "Podcasts & Audio Learning",
      displayName: "Podcasts",
      pmEnabled: false
    }
  ];

  // Frontend-only: hide "Skill Building" tab for now (can be re-enabled later)
  const visibleContentTypes = newContentTypes.filter(
    (type) => type.displayName !== "Skill Building"
  );

  // Create tab order from the visible content types using displayName
  const tabOrder = visibleContentTypes.map((type) =>
    type.displayName.toLowerCase()
  );

  // Function to get content type ID(s) from the tab name
  const getContentTypeId = (tabName: string, contentTypes: any) => {
    // Find the content type from our new content types array using displayName
    const newContentType = newContentTypes.find(
      (ct) => ct.displayName.toLowerCase() === tabName.toLowerCase()
    );
    
    if (newContentType) {
      return newContentType.id;
    }
    
  };

  // Render the appropriate component based on activeTab
  const renderTabContent = () => {
    if (activeTab === "latest") {
      return <ForYouSection contentTypeHandler={setActiveTab} />;
    } else {
      return (
        <NewContentTypeSection
          contentType={activeTab}
          contentTypeId={getContentTypeId(activeTab, contentTypes)}
        />
      );
    }
  };

  // Helper function to get token
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  // Fetch initial bookmarked and liked content
  useEffect(() => {
    const fetchInitialContent = async () => {
      try {
        const token = getToken();

        // Fetch bookmarked content
        const bookmarkedResponse = await getLikeBookmarkedContent(
          "bookmarked",
          token
        );
        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
        }

        // Fetch liked content
        const likedResponse = await getLikeBookmarkedContent("liked", token);
        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
        }
      } catch (error) {
        console.error("Error fetching initial content:", error);
      }
    };

    if (tatvaPediaToken) {
      fetchInitialContent();
    }
  }, [tatvaPediaToken]);

  // Add effect to scroll to active tab when it changes
  useEffect(() => {
    if (activeTab && tabRefs.current[activeTab]) {
      tabRefs.current[activeTab]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);
  
  // Handle click outside dropdown and escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDropdownOpen]);

  return (
    <div className="bg-[#FAFAFA] flex flex-col h-screen overflow-auto pb-20">
      {/* Header */}
      <div className="bg-white p-3 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center">
          <img src={pediaLogo} alt={pediaLabel} className="h-[27.63px]" />
        </div>
        <div className="relative">
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            onClick={() => {
              trackZymedEvent(ZYMED_EVENTS.PEDIA_PERSONALISE_FEED_OPENED);
              navigate("/tatvapedia/personalize-feed", { state: { activeTab } });
            }}
            aria-label="Open Filters"
          >
            <Edit className="h-3 w-3 stroke-gray-500" />
            <span className="text-sm font-normal text-gray-700">Edit feed</span>
          </button>
          {/* <span className="absolute top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full"></span> */}
        </div>
      </div>

      {/* Horizontal tabs with dropdown */}
      <div className="shadow-md bg-white">
        <div className="flex items-center">
          <div
            ref={tabsContainerRef}
            className="flex gap-2 px-2 overflow-x-auto hide-scrollbar flex-1"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {/* Latest tab */}
            <button
              ref={(el) => (tabRefs.current["latest"] = el)}
              onClick={() => selectTabFromPills("latest")}
              className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${
                activeTab === "latest" ? "text-indigo-600" : ""
              }`}
            >
              <span
                className={`flex w-max items-center font-roboto text-base px-4 py-2 leading-6 tracking-[0.25px] ${
                  activeTab === "latest"
                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                    : ""
                }`}
              >
                Latest
              </span>
              {activeTab === "latest" && (
                <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
              )}
            </button>

            {/* Content type tabs */}
            {tabOrder.map((tabType) => {
              const isActive = activeTab === tabType;
              const displayName = tabType
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              return (
                <button
                  key={tabType}
                  ref={(el) => (tabRefs.current[tabType] = el)}
                  onClick={() => selectTabFromPills(tabType)}
                  className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg whitespace-nowrap ${
                    isActive ? "text-indigo-600" : ""
                  }`}
                >
                  <span
                    className={`flex w-max items-center font-roboto text-base px-4 py-2 leading-6 tracking-[0.25px] ${
                      isActive
                        ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                        : ""
                    }`}
                  >
                    {displayName}
                  </span>
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Dropdown Filter Button */}
          <div className="relative px-2" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-center w-[47px] h-[52px] bg-white shadow-[0px_2px_12px_0px_#00000014] hover:bg-gray-50 transition-colors"
              aria-label="Filter Options"
            >
              <svg
                className={`w-[26px] h-[26px] text-[#4F4F4F] transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute top-full right-2 mt-1 w-[225px] bg-white rounded-[16px] border border-[#0000000D] shadow-[0px_0px_25px_0px_#0000000D] z-50">
                <div className="flex flex-col gap-2 pt-3 pb-3">
                  {/* Latest Option */}
                  <button
                    onClick={() => {
                      selectTabFromDropdown("latest");
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm ${
                      activeTab === "latest" ? "font-semibold text-[#4B4AD5]" : "text-gray-700"
                    }`}
                  >
                    Latest
                  </button>
                  
                  {/* Other Filter Options (hide Skill Building for now) */}
                  {visibleContentTypes.map((contentType) => {
                    const tabType = contentType.displayName.toLowerCase();
                    const isActive = activeTab === tabType;
                    
                    return (
                      <button
                        key={contentType.id}
                        onClick={() => {
                          selectTabFromDropdown(tabType);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors text-sm ${
                          isActive ? "font-semibold text-[#4B4AD5]" : "text-gray-700"
                        }`}
                      >
                        {contentType.displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Render the appropriate component based on activeTab */}
      {renderTabContent()}

      <NavBar />
    </div>
  );
}

export default memo(TatvaPediaHomeScreen);
